import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

function InstructorProfile() {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [stats, setStats] = useState({
    totalWorkshops: 0,
    totalStudents: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [copiedProfileLink, setCopiedProfileLink] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchProfileData();
    }
  }, [currentUser]);

  const fetchProfileData = async () => {
    try {
      // Fetch profile
      const profileDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (profileDoc.exists()) {
        setProfile(profileDoc.data());
      }

      // Fetch workshops
      const workshopsQuery = query(
        collection(db, 'sessions_workshops'),
        where('instructorId', '==', currentUser.uid),
        where('status', '==', 'published')
      );
      const workshopsSnapshot = await getDocs(workshopsQuery);
      const workshopsData = workshopsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch bookings for stats
      const bookingsQuery = query(
        collection(db, 'sessions_bookings'),
        where('instructorId', '==', currentUser.uid)
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookingsData = bookingsSnapshot.docs.map(doc => doc.data());

      // Calculate stats
      const totalRevenue = bookingsData
        .filter(b => b.status === 'confirmed')
        .reduce((sum, booking) => sum + (booking.totalAmount || 0), 0);

      const totalStudents = bookingsData.filter(b => b.status === 'confirmed').length;

      setWorkshops(workshopsData);
      setStats({
        totalWorkshops: workshopsData.length,
        totalStudents,
        totalRevenue
      });
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyProfileLink = async () => {
    const link = `${window.location.origin}/?instructor=${currentUser.uid}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedProfileLink(true);
      setTimeout(() => setCopiedProfileLink(false), 2000);
    } catch (error) {
      console.error('Failed to copy profile link:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'TBD';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-klorah-dark flex items-center justify-center">
        <div className="text-white">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-klorah-dark flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div className="glass-panel-strong p-8 rounded-xl">
            <div className="w-16 h-16 bg-gradient-to-br from-klorah-primary to-klorah-secondary mx-auto mb-6 flex items-center justify-center rounded-full">
              <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-4 tracking-wider">PROFILE NOT FOUND</h3>
            <p className="text-klorah-text mb-6">Complete your profile to start teaching workshops.</p>
            <Link
              to="/edit-profile"
              className="klorah-button inline-flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              COMPLETE PROFILE
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-klorah-dark">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Profile Header */}
        <div className="glass-panel p-8 mb-8 rounded-xl">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-8 lg:space-y-0">
            <div className="flex flex-col md:flex-row md:items-center space-y-6 md:space-y-0 md:space-x-8">
              {/* Profile Image */}
              <div className="w-32 h-32 bg-gradient-to-br from-klorah-primary to-klorah-secondary border border-klorah-border flex items-center justify-center flex-shrink-0 rounded-full overflow-hidden">
                {profile.profileImage ? (
                  <img 
                    src={profile.profileImage} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-4xl font-medium text-black">
                    {profile.firstName?.[0] || currentUser?.email?.[0]?.toUpperCase() || '?'}
                  </span>
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-1">
                <div className="space-y-4">
                  <div>
                    <h1 className="text-4xl font-bold text-white mb-2">
                      {profile.firstName && profile.lastName 
                        ? `${profile.firstName} ${profile.lastName}`
                        : 'Instructor Profile'
                      }
                    </h1>
                    {profile.title && (
                      <p className="text-xl text-klorah-primary font-medium">{profile.title}</p>
                    )}
                    {profile.experience && (
                      <p className="text-klorah-text">{profile.experience} of experience</p>
                    )}
                  </div>

                  {profile.bio && (
                    <p className="text-klorah-text leading-relaxed max-w-2xl">
                      {profile.bio}
                    </p>
                  )}

                  {profile.specialties && profile.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {profile.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="inline-flex px-3 py-1 bg-klorah-primary/10 border border-klorah-primary/30 text-klorah-primary text-sm rounded-full"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Actions */}
            <div className="flex flex-col space-y-3">
              <Link
                to="/edit-profile"
                className="klorah-button inline-flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                EDIT PROFILE
              </Link>
              
              <button
                onClick={copyProfileLink}
                className="inline-flex items-center px-6 py-3 bg-klorah-card border border-klorah-border text-klorah-text hover:text-white rounded-xl transition-all text-sm font-medium tracking-wider"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                {copiedProfileLink ? 'COPIED!' : 'SHARE PROFILE'}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass-panel p-6 rounded-xl">
            <h3 className="text-sm font-medium text-klorah-text-light mb-1 tracking-wider">TOTAL WORKSHOPS</h3>
            <p className="text-2xl font-bold text-klorah-primary">{stats.totalWorkshops}</p>
          </div>
          <div className="glass-panel p-6 rounded-xl">
            <h3 className="text-sm font-medium text-klorah-text-light mb-1 tracking-wider">TOTAL STUDENTS</h3>
            <p className="text-2xl font-bold text-klorah-primary">{stats.totalStudents}</p>
          </div>
          <div className="glass-panel p-6 rounded-xl">
            <h3 className="text-sm font-medium text-klorah-text-light mb-1 tracking-wider">TOTAL REVENUE</h3>
            <p className="text-2xl font-bold text-klorah-primary">{formatPrice(stats.totalRevenue)}</p>
          </div>
        </div>

        {/* Published Workshops */}
        <div className="glass-panel border border-klorah-border/30 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-klorah-border/30">
            <h2 className="text-2xl font-bold text-white tracking-wider">PUBLISHED WORKSHOPS</h2>
          </div>
          
          <div className="p-6">
            {workshops.length > 0 ? (
              <div className="space-y-6">
                {workshops.map((workshop) => (
                  <div key={workshop.id} className="glass-panel-subtle border border-klorah-border/30 rounded-xl p-6 hover:bg-klorah-card/30 transition-all">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">{workshop.title}</h3>
                        <p className="text-klorah-text mb-2">{workshop.location} • {formatDate(workshop.startDate)}</p>
                        <p className="text-klorah-text-light text-sm">
                          {workshop.currentParticipants || 0} / {workshop.maxParticipants} participants
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-klorah-primary">{formatPrice(workshop.price || 0)}</p>
                        <Link
                          to={`/edit-workshop/${workshop.id}`}
                          className="text-sm text-klorah-primary hover:text-white transition-colors"
                        >
                          Edit Workshop
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-klorah-card/50 mx-auto mb-6 flex items-center justify-center rounded-full">
                  <svg className="w-8 h-8 text-klorah-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-4 tracking-wider">NO PUBLISHED WORKSHOPS</h3>
                <p className="text-klorah-text mb-6">Create and publish your first workshop to start teaching.</p>
                <Link
                  to="/create-workshop"
                  className="klorah-button inline-flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  CREATE WORKSHOP
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default InstructorProfile; 